import React, { useEffect, useRef, useState } from "react";
import { Mail, Phone, MapPin, Send, CheckCircle2, AlertCircle } from "lucide-react";
import { SITE, INQUIRY_TYPES } from "@/lib/site";
import { submitContact } from "@/lib/api";
import ScheduleAssessment from "@/components/site/ScheduleAssessment";
import Turnstile from "@/components/site/Turnstile";

const FAQ = [
  {
    q: "What exactly is \"preventive presence\"?",
    a: "Preventive presence is our quiet, doctrine-led practice of placing trained canine-handler teams in environments before tension escalates. The dogs do not diagnose, counsel, or treat — they lower physiological arousal, soften posture, and strengthen the protective factors that make other care work. It is cumulative, not dramatic.",
  },
  {
    q: "Do your dogs provide therapy?",
    a: "No. MSWDC is explicitly non-clinical. We do not diagnose or treat, and we never replace therapists, counselors, or behavioral specialists. We stabilize the room their care is delivered in.",
  },
  {
    q: "What does the \"22\" in your logo represent?",
    a: "The 22 is a solemn remembrance of lives lost to suicide — particularly within our veteran community. We call this the Sentinel 22 principle. It is not used for emotional leverage; it is a reminder of urgency and humility, and it disciplines us never to over-promise.",
  },
  {
    q: "How do you protect the welfare of your working dogs?",
    a: "Welfare is the ethical ceiling of the entire institution. Our partners are governed by cumulative-stress monitoring, deployment tiers, mandatory recovery, humane (force-free) training, joy preservation, and lifetime stewardship — from selection through dignified retirement.",
  },
  {
    q: "What kinds of organizations do you partner with?",
    a: "Schools, veteran communities and events, rural facilities, behavioral health systems, and youth residential programs — settings where escalation carries high operational and human cost and where preventive presence has the most leverage.",
  },
  {
    q: "How is the Coalition funded?",
    a: "Through a diversified base — individual donors, monthly partners, major gifts, planned giving, grants, and aligned partnerships. We organize our financial stewardship around a Three-Circle Capital Model (Operational, Stabilization, Legacy) to safeguard our independence and our freedom to refuse misaligned support.",
  },
  {
    q: "Do you offer licensing or doctrine partnerships?",
    a: "Yes. For permissions, licensing, or partnership inquiries, please select the appropriate inquiry type in the form and our team will respond.",
  },
];

export default function ContactPage() {
  useEffect(() => {
    document.title = "Contact · MSWDC";
  }, []);

  const [form, setForm] = useState({
    name: "",
    organization: "",
    role: "",
    email: "",
    phone: "",
    inquiry_type: "General",
    message: "",
  });
  const [status, setStatus] = useState({ state: "idle", error: "" });
  const [openIdx, setOpenIdx] = useState(0);
  const [captchaToken, setCaptchaToken] = useState(null);
  const captchaEnabled = !!process.env.REACT_APP_TURNSTILE_SITE_KEY;
  const formRef = useRef(null);

  const handleAssessmentComplete = (picks) => {
    const prefill = `Sector: ${picks.sector?.label || "—"}
Environment size: ${picks.size?.label || "—"} ${picks.size?.note ? `(${picks.size.note})` : ""}
Preferred timeframe: ${picks.window?.label || "—"}

Briefly describe what you'd like the Coalition to know:
`;
    setForm((f) => ({
      ...f,
      inquiry_type: "Partnership",
      message: prefill,
    }));
    setTimeout(() => {
      formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      const nameField = document.querySelector('[data-testid="contact-input-name"]');
      nameField?.focus();
    }, 100);
  };

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (captchaEnabled && !captchaToken) {
      setStatus({ state: "error", error: "Please complete the captcha." });
      return;
    }
    setStatus({ state: "loading", error: "" });
    try {
      await submitContact({
        ...form,
        organization: form.organization || null,
        role: form.role || null,
        phone: form.phone || null,
        turnstile_token: captchaToken || undefined,
      });
      setStatus({ state: "success", error: "" });
      setForm({
        name: "",
        organization: "",
        role: "",
        email: "",
        phone: "",
        inquiry_type: "General",
        message: "",
      });
      setCaptchaToken(null);
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        err?.message ||
        "Something went wrong. Please try again.";
      setStatus({ state: "error", error: String(msg) });
    }
  };

  return (
    <main data-testid="contact-page" className="bg-white">
      {/* Header */}
      <section className="border-b border-slate-200">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16 pt-24 md:pt-32 pb-12 md:pb-16">
          <div className="flex items-center gap-3">
            <span className="brand-rule" />
            <span className="overline">Contact the Coalition</span>
          </div>
          <h1 className="mt-6 font-display font-black text-4xl sm:text-5xl md:text-7xl tracking-tighter leading-[1.02] text-slate-900 max-w-4xl">
            Start a conversation about your environment.
          </h1>
          <p className="mt-6 max-w-2xl text-slate-600 text-base md:text-lg leading-relaxed">
            Partnerships, licensing, press, and general inquiries — use the form below or
            reach us directly. We respond to qualified inquiries within two business days.
          </p>
        </div>
      </section>

      {/* Schedule a Site Assessment */}
      <section className="bg-slate-50 border-b border-slate-200">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16 py-16 md:py-20">
          <ScheduleAssessment onComplete={handleAssessmentComplete} />
        </div>
      </section>

      {/* Split contact */}
      <section className="bg-white">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16 py-16 md:py-24">
          <div className="grid grid-cols-12 gap-10 lg:gap-20">
            {/* Left: info */}
            <aside className="col-span-12 md:col-span-5 lg:col-span-4">
              <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-amber-600">
                Direct Channels
              </div>
              <ul className="mt-8 space-y-8">
                <li className="flex items-start gap-4">
                  <Mail size={20} className="text-slate-900 mt-1 shrink-0" strokeWidth={1.5} />
                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500">Email</div>
                    <a
                      href={`mailto:${SITE.email}`}
                      data-testid="contact-email-link"
                      className="mt-1 inline-block link-underline text-slate-900 text-base md:text-lg break-all"
                    >
                      {SITE.email}
                    </a>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <Phone size={20} className="text-slate-900 mt-1 shrink-0" strokeWidth={1.5} />
                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500">Phone</div>
                    <a
                      href={SITE.phoneHref}
                      data-testid="contact-phone-link"
                      className="mt-1 inline-block link-underline text-slate-900 text-base md:text-lg"
                    >
                      {SITE.phone}
                    </a>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <MapPin size={20} className="text-slate-900 mt-1 shrink-0" strokeWidth={1.5} />
                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500">Headquarters</div>
                    <address
                      data-testid="contact-address"
                      className="mt-1 not-italic text-slate-900 text-base md:text-lg leading-snug"
                    >
                      {SITE.address}
                    </address>
                  </div>
                </li>
              </ul>

              <div className="mt-12 border border-slate-200 bg-slate-50 p-6">
                <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-slate-500">
                  Hours
                </div>
                <p className="mt-3 text-slate-700 leading-relaxed text-[15px]">
                  Monday – Friday · 09:00 – 17:00 AKST
                  <br />
                  Inquiries reviewed in deployment order.
                </p>
              </div>
            </aside>

            {/* Right: form */}
            <div className="col-span-12 md:col-span-7 lg:col-span-8">
              <form
                onSubmit={onSubmit}
                data-testid="contact-form"
                className="border border-slate-200 p-8 md:p-12 bg-white"
              >
                <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-amber-600">
                  Inquiry Form
                </div>
                <h2 className="mt-4 font-display font-black text-2xl md:text-4xl tracking-tight text-slate-900">
                  Tell us what you're working on.
                </h2>

                <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
                  <div>
                    <label className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500">
                      Full Name *
                    </label>
                    <input
                      required
                      name="name"
                      value={form.name}
                      onChange={onChange}
                      placeholder="Jane Doe"
                      className="mswdc-input"
                      data-testid="contact-input-name"
                    />
                  </div>
                  <div>
                    <label className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500">
                      Email *
                    </label>
                    <input
                      required
                      type="email"
                      name="email"
                      value={form.email}
                      onChange={onChange}
                      placeholder="jane@institution.org"
                      className="mswdc-input"
                      data-testid="contact-input-email"
                    />
                  </div>
                  <div>
                    <label className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500">
                      Organization
                    </label>
                    <input
                      name="organization"
                      value={form.organization}
                      onChange={onChange}
                      placeholder="Anchorage School District"
                      className="mswdc-input"
                      data-testid="contact-input-organization"
                    />
                  </div>
                  <div>
                    <label className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500">
                      Role
                    </label>
                    <input
                      name="role"
                      value={form.role}
                      onChange={onChange}
                      placeholder="Director of Student Services"
                      className="mswdc-input"
                      data-testid="contact-input-role"
                    />
                  </div>
                  <div>
                    <label className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500">
                      Phone
                    </label>
                    <input
                      name="phone"
                      value={form.phone}
                      onChange={onChange}
                      placeholder="(907) 000-0000"
                      className="mswdc-input"
                      data-testid="contact-input-phone"
                    />
                  </div>
                  <div>
                    <label className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500">
                      Inquiry Type *
                    </label>
                    <select
                      name="inquiry_type"
                      required
                      value={form.inquiry_type}
                      onChange={onChange}
                      data-testid="contact-select-inquiry"
                      className="mswdc-input appearance-none"
                    >
                      {INQUIRY_TYPES.map((t) => (
                        <option key={t} value={t}>
                          {t}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="md:col-span-2">
                    <label className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500">
                      Message *
                    </label>
                    <textarea
                      name="message"
                      required
                      rows={6}
                      value={form.message}
                      onChange={onChange}
                      placeholder="Briefly describe your environment and what you're hoping to explore with the Coalition."
                      className="mswdc-input resize-none"
                      data-testid="contact-input-message"
                    />
                  </div>
                </div>

                <div className="mt-10 flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-4">
                  <p className="text-xs text-slate-500 max-w-md">
                    By submitting, you agree to be contacted regarding your inquiry.
                    We will never share your information.
                  </p>
                  <button
                    type="submit"
                    disabled={status.state === "loading"}
                    data-testid="contact-submit-button"
                    className="group inline-flex items-center justify-center gap-3 bg-slate-900 hover:bg-amber-600 disabled:opacity-60 disabled:cursor-not-allowed text-white px-8 py-4 font-semibold text-sm transition-colors"
                  >
                    {status.state === "loading" ? "Sending…" : "Send inquiry"}
                    <Send size={15} className="group-hover:translate-x-1 transition-transform" />
                  </button>
                </div>

                {captchaEnabled && (
                  <div className="mt-6">
                    <Turnstile onToken={setCaptchaToken} />
                  </div>
                )}

                {status.state === "success" && (
                  <div
                    data-testid="contact-success"
                    className="mt-8 flex items-start gap-3 border border-emerald-200 bg-emerald-50 text-emerald-900 p-5"
                  >
                    <CheckCircle2 size={20} className="mt-0.5 shrink-0" />
                    <div>
                      <div className="font-display font-bold">Inquiry received.</div>
                      <p className="text-sm mt-1">
                        Thank you — a member of the Coalition will respond within two
                        business days.
                      </p>
                    </div>
                  </div>
                )}

                {status.state === "error" && (
                  <div
                    data-testid="contact-error"
                    className="mt-8 flex items-start gap-3 border border-rose-200 bg-rose-50 text-rose-900 p-5"
                  >
                    <AlertCircle size={20} className="mt-0.5 shrink-0" />
                    <div>
                      <div className="font-display font-bold">Submission failed.</div>
                      <p className="text-sm mt-1">{status.error}</p>
                    </div>
                  </div>
                )}
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-slate-50 border-t border-slate-200">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16 py-24 md:py-28">
          <div className="grid grid-cols-12 gap-8">
            <div className="col-span-12 md:col-span-4">
              <div className="flex items-center gap-3">
                <span className="brand-rule" />
                <span className="overline">FAQ</span>
              </div>
              <h2 className="mt-6 font-display font-black text-3xl md:text-5xl tracking-tight leading-[1.05] text-slate-900">
                Questions we hear often.
              </h2>
            </div>
            <div className="col-span-12 md:col-span-8">
              <ul className="border-t border-slate-200">
                {FAQ.map((item, i) => {
                  const open = openIdx === i;
                  return (
                    <li key={i} className="border-b border-slate-200">
                      <button
                        type="button"
                        data-testid={`faq-toggle-${i}`}
                        onClick={() => setOpenIdx(open ? -1 : i)}
                        className="w-full flex items-start justify-between gap-6 py-6 text-left"
                      >
                        <span className="font-display font-bold text-lg md:text-xl text-slate-900">
                          {item.q}
                        </span>
                        <span className="font-mono text-amber-600 text-sm pt-1 shrink-0">
                          {open ? "—" : "+"}
                        </span>
                      </button>
                      {open && (
                        <p
                          data-testid={`faq-answer-${i}`}
                          className="pb-6 pr-10 text-slate-600 leading-relaxed -mt-1"
                        >
                          {item.a}
                        </p>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
